import { LineRemark } from "@/js/interfaces/DTOs/Models";

export class LineRemarkBag {
    private readonly remarks: Map<number, Array<LineRemark>> = new Map();
    private _cache: Array<Array<LineRemark>> | null = null;

    constructor(lineRemarks: Record<number, Array<LineRemark>> = {}) {
        for (const [lineId, remarks] of Object.entries(lineRemarks)) {
            this.remarks.set(Number(lineId), remarks);
        }
    }

    /**
     * Get all line remarks in the bag.
     */
    public all(): Array<Array<LineRemark>> {
        if (null === this._cache) {
            this._cache = Array.from(this.remarks.values());
        }
        return this._cache;
    }

    /**
     * Retrieve line remarks for a given line id.
     *
     * @param lineId The line id to find where the remark is stored
     */
    public get(lineId: number): Array<LineRemark> {
        return this.getOrFail(lineId);
    }

    /**
     * Retrieve a quantity of line remarks for a given line id.
     *
     * @param lineId The line id to find where the remark is stored
     * @param quantity The quantity of line remarks to take for the given line id
     */
    public take(lineId: number, quantity: number): Array<LineRemark> {
        return this.getOrFail(lineId).slice(0, quantity);
    }

    /**
     * Retrieve the line remarks associated with a line.
     *
     * @param lineId The line id to find where the remark is stored
     */
    private getOrFail(lineId: number): Array<LineRemark> {
        const item = this.remarks.get(lineId);
        if (item === undefined) {
            throw new Error(`No line remarks found for line id ${lineId}`);
        }
        return item;
    }

    /**
     * Check if there is line remark for a given line id
     *
     * @param lineId The line id to find where the remark is stored
     */
    public has(lineId: number): boolean {
        return this.remarks.has(lineId);
    }

    /**
     * Add a line remark to the bag
     *
     * @param lineId The line id to find where the remark is stored
     * @param newRemark The line remark to add to the bag
     */
    public add(lineId: number, newRemark: LineRemark): void {
        if (!this.remarks.has(lineId)) {
            this.remarks.set(lineId, []);
        }
        const existing = this.remarks.get(lineId)!;
        if (existing.some(remark => remark.id === newRemark.id)) {
            throw new Error(`Trying to add a line remark with id ${newRemark.id} for line id ${lineId} that is already in the bag`);
        }
        existing.push(newRemark);
        this._cache = null;
    }

    /**
     * Delete a line remark from the bag
     *
     * @param lineId The line id to find where the remark is stored
     * @param remarkId The id of the line remark to delete
     */
    public delete(lineId: number, remarkId: number): void {
        const remarksForLine = this.remarks.get(lineId);
        if (undefined === remarksForLine) {
            throw new Error(`Trying to delete a line remark for line id ${lineId} that is not in the bag`);
        }
        const remarkIndex = remarksForLine.findIndex(remark => remark.id === remarkId);
        if (-1 === remarkIndex) {
            throw new Error(`Trying to delete a line remark with id ${remarkId} for line id ${lineId} that is not in the bag`);
        }
        remarksForLine.splice(remarkIndex, 1);
        if (0 === remarksForLine.length) {
            this.remarks.delete(lineId);
        }
        this._cache = null;
    }

    /**
     * Empty line remarks for a given line id
     *
     * @param lineId The line id to find where the remark is stored
     */
    public emptyRemarksForLine(lineId: number): void {
        if (!this.remarks.has(lineId)) {
            throw new Error(`Trying to empty line remarks for line id ${lineId} that is not in the bag`);
        }
        this.remarks.delete(lineId);
        this._cache = null;
    }

    /**
     * Count the total of line remarks in the bag
     */
    public count(): number {
        let total = 0;
        for (const remarks of this.remarks.values()) {
            total += remarks.length;
        }
        return total;
    }

    /**
     * Check if there is no line remark in the bag
     */
    public isEmpty(): boolean {
        return 0 === this.remarks.size;
    }

    /**
    * Check if there is no line remark for a given line id
    * @param lineId The line id to find where the remark is stored
    */
    public isEmptyForLine(lineId: number): boolean {
        const remarksForLine = this.remarks.get(lineId);
        return undefined === remarksForLine || 0 === remarksForLine.length;
    }

    /**
    * Check if there is more than a given quantity of line remarks for a given line id
    * @param lineId The line id to find where the remark is stored
    * @param quantity The quantity to compare with the number of line remarks for the given line id
    */
    public isLineHasMoreThanQuantityOfRemarks(lineId: number, quantity: number): boolean {
        const remarksForLine = this.remarks.get(lineId);
        return undefined !== remarksForLine && remarksForLine.length > quantity;
    }
}